import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useEffect, useContext, useState, lazy, Suspense } from "react";
import Box from "@mui/material/Box";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { fetchAuthSession } from "aws-amplify/auth";
import NavBar from "./components/NavBar";
import ApplicantsList from "./pages/ApplicantsList";
import Applicant from "./pages/Applicant";
import DataContextProvider, {
  DataContext,
  fetchApplicantsList,
} from "./DataContextProvider";
import TeacherView from "./pages/TeacherView";
// Code-split the Admin dashboard: its chart/map libraries (echarts, x-charts)
// are ~250KB gzip and admin-only, so they must not weigh down the initial
// bundle that every recommender/grader loads. Declared after the imports
// (below) to satisfy import/first.
import "@aws-amplify/ui-react/styles.css";
import "./override-amplify.css";
import SignInComponent from "./components/SignInComponent";
import {
  AuthGroupsContext,
  isAdminOrGraderByGroups,
} from "./auth-groups";

// Lazy-loaded as its own chunk, fetched only when an admin navigates to
// /dashboard (keeps echarts/x-charts out of the initial bundle).
const Dashboard = lazy(() => import("./pages/Dashboard"));

const ViewerAppContent = () => {
  const { dispatch } = useContext(DataContext);

  useEffect(() => {
    if (dispatch) fetchApplicantsList(dispatch);
  }, [dispatch]);
  return (
    <div className="App" style={{ width: "100%", height: "100%" }}>
      <Router>
        <NavBar />
        <Box height="calc(100% - 64px)">
          <Routes>
            <Route
              path="/dashboard"
              element={
                <Suspense fallback={null}>
                  <Dashboard />
                </Suspense>
              }
            />
            <Route path="/:id" element={<Applicant />} />
            <Route path="/" element={<ApplicantsList />} />
          </Routes>
        </Box>
      </Router>
    </div>
  );
};

const TeacherAppContent = () => {
  return (
    <div className="App" style={{ width: "100%", height: "100%" }}>
      <Router>
        <NavBar />
        <Box height="calc(100% - 64px)">
          <Routes>
            <Route path="/" element={<TeacherView />} />
          </Routes>
        </Box>
      </Router>
    </div>
  );
};

// `cognito:groups` arrives in the JWT id-token payload as either a string[] or
// (rarely) a single string. Normalize defensively; treat anything else as
// "no groups". Exported so the #144 test scaffolding can pin the contract:
// the only producer of the `groups: string[]` shape every UX gate keys off.
export const extractGroups = (claim: unknown): string[] => {
  if (Array.isArray(claim)) {
    return claim.filter((g): g is string => typeof g === "string");
  }
  if (typeof claim === "string") {
    return [claim];
  }
  return [];
};

const App = () => {
  const { route } = useAuthenticator((context) => [context.route]);
  // null = session not yet resolved (initial mount or post-login refresh).
  // string[] = the `cognito:groups` claim, normalized via extractGroups
  // (operator answer Q6 on #143). Stored as the raw groups -- not just a
  // derived boolean -- so descendants reading `AuthGroupsContext` can
  // make their own decisions (Admin-only vs Admin-or-Grader vs
  // Recommender) without re-fetching the session.
  const [groups, setGroups] = useState<string[] | null>(null);

  useEffect(() => {
    if (route !== "authenticated") {
      setGroups(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const session = await fetchAuthSession();
        const nextGroups = extractGroups(
          session.tokens?.idToken?.payload["cognito:groups"],
        );
        if (!cancelled) {
          setGroups(nextGroups);
        }
      } catch (err) {
        // Failing closed: if we can't read the session, treat the user as a
        // recommender (the lower-privilege role) by leaving the groups
        // empty -- isAdminOrGraderByGroups([]) returns false. The
        // Authenticator wrapper will surface auth errors separately via
        // `route`.
        // eslint-disable-next-line no-console
        console.error("[gbfdx] failed to read auth session", err);
        if (!cancelled) setGroups([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [route]);

  // For admin and graders
  const ViewerApp = () => (
    <DataContextProvider>
      <ViewerAppContent />
    </DataContextProvider>
  );

  // For teachers
  const TeacherApp = () => (
    <DataContextProvider>
      <TeacherAppContent />
    </DataContextProvider>
  );

  if (route !== "authenticated") {
    return <SignInComponent />;
  }
  // Briefly null while the session resolves; avoids a flash of TeacherView
  // for admins/graders on first paint.
  if (groups === null) {
    return null;
  }
  // The provider scope intentionally wraps both ViewerApp and TeacherApp
  // so descendants in either tree can call `useAuthGroups()`. The
  // routing decision below uses the same predicate any descendant would
  // -- both layers read from the same authoritative source (the
  // session's groups).
  return (
    <AuthGroupsContext.Provider value={groups}>
      {isAdminOrGraderByGroups(groups) ? <ViewerApp /> : <TeacherApp />}
    </AuthGroupsContext.Provider>
  );
};

export default App;
